/**
 * SUB-3.4 · FR26. The what-to-copy picker's coherence rules, as a pure function.
 *
 * ⚠️ THIS IS NOT THE ENFORCEMENT LAYER AND MUST NEVER BECOME IT.
 * `clone_tenant_config` re-derives every rule below and refuses an incoherent
 * selection with `23514`, whoever calls it and however they got there. What lives
 * here is the sentence shown next to the checkbox that caused the problem, so a user
 * sees a reason instead of a database error.
 *
 * It was extracted out of `CreateSubAccountDialog` for one reason: the rule was only
 * verifiable by opening the dialog in a browser and looking at it. The refusals have
 * a live SQL suite (`supabase/tests/verify_sub_3_4_dependencies.sql`); the UI half
 * had nothing, so a future edit could have inverted a condition and no gate would
 * have noticed. `copyCategoryDependencies.test.ts` is that gate. What remains
 * browser-only is whether the sentence is rendered, not whether it is computed.
 */

export const COPY_CATEGORIES = [
  { id: 'pipelines', label: 'Pipelines and stages' },
  { id: 'checklists', label: 'Checklists and checklist templates' },
  { id: 'notifications', label: 'Notification templates' },
  { id: 'theme', label: 'Theme and branding' },
  { id: 'dashboards', label: 'Dashboards and widgets' },
  { id: 'settings', label: 'Settings' },
] as const;

export type CopyCategory = (typeof COPY_CATEGORIES)[number]['id'];

/**
 * FR26's edges, as measured against `clone_tenant_config`'s id maps.
 *
 * Three of the four the server enforces, and the fourth is deliberately absent:
 *
 *   checklists -> pipelines   `checklist_templates.pipeline_id` / `.stage_id`
 *   dashboards -> pipelines   `pipelineIds`, `stageIds`, `hiddenStageIds`,
 *                             `installCompleteStageIds` in widget config
 *   dashboards -> checklists  SUB-3.4. `checklistItemId` / `field` and the composite
 *                             `checklist_<id>` under `columns[].id`, which
 *                             DASH-12.2's checklist filters write
 *
 * ⚠️ THE UI IS STRICTER THAN THE SERVER ON THE THIRD EDGE, AND ONLY IN THAT
 * DIRECTION. The server refuses `dashboards` without `checklists` only when the
 * PARENT's widgets actually carry such a reference, which it can see and the browser
 * cannot without another round trip. Listing it unconditionally costs an account with
 * no checklist filters one extra ticked box; staying silent would submit a request
 * the server may refuse for a reason the user was never shown.
 *
 * ⚠️ THE FOURTH EDGE, `pipelines -> checklists`, IS NOT LISTED ON PURPOSE. A default
 * pipeline view can show a checklist column and the server refuses that case too, but
 * it was measured at 0 of 32 live `is_system_default` views against 32 live cases for
 * the dashboards edge. When it does occur the server's message is rendered in place.
 */
export const COPY_CATEGORY_DEPENDENCIES: Partial<Record<CopyCategory, CopyCategory[]>> = {
  checklists: ['pipelines'],
  dashboards: ['pipelines', 'checklists'],
};

/**
 * FR26 / AC1's "with the reason stated". Keyed `dependent->needed` so each edge says
 * why IT specifically breaks, rather than one sentence about pipelines that is wrong
 * for the checklists edge.
 */
export const COPY_CATEGORY_DEPENDENCY_REASONS: Record<string, string> = {
  'checklists->pipelines':
    'Checklist templates are attached to a pipeline and a stage, so copying them without pipelines would leave those links broken.',
  'dashboards->pipelines':
    'Widgets filter by pipeline and stage, so copying dashboards without pipelines would leave those filters pointing at nothing.',
  'dashboards->checklists':
    'Widgets can filter on and display checklist fields, so copying dashboards without checklists would leave those columns pointing back at your own account.',
};

export interface UnmetDependency {
  dependent: CopyCategory;
  needed: CopyCategory;
  /** The sentence to show. Never empty: a generic fallback covers a new edge whose
   *  reason has not been written yet, so adding an edge cannot produce a blank. */
  reason: string;
}

const FALLBACK_REASON =
  'copying one without the other would leave broken links.';

/**
 * Every dependency the selection fails to satisfy, in a stable order.
 *
 * ⚠️ ONE PASS, NOT A TRANSITIVE CLOSURE. `dashboards` names both `pipelines` and
 * `checklists` explicitly rather than relying on `checklists -> pipelines` to carry
 * the first, so one pass is complete today. If a fourth edge is added, list every
 * ancestor rather than trusting a walk that does not exist.
 *
 * ⚠️ AN UNKNOWN TOKEN IS IGNORED, NOT REPORTED. This function answers "what is
 * missing", and an unknown category is a different question with a different owner:
 * `createSubAccount` rejects it before the call and `clone_tenant_config` raises
 * `22023` on it. Reporting it here would give one mistake two error messages.
 */
export function findUnmetDependencies(selected: readonly CopyCategory[]): UnmetDependency[] {
  const chosen = new Set(selected);
  const out: UnmetDependency[] = [];

  // Iterated over COPY_CATEGORIES rather than over the dependency map's own keys, so
  // the order the user sees matches the order of the checkboxes rather than an
  // object's insertion order.
  for (const { id: dependent } of COPY_CATEGORIES) {
    if (!chosen.has(dependent)) continue;
    for (const needed of COPY_CATEGORY_DEPENDENCIES[dependent] ?? []) {
      if (chosen.has(needed)) continue;
      out.push({
        dependent,
        needed,
        reason: COPY_CATEGORY_DEPENDENCY_REASONS[`${dependent}->${needed}`] ?? FALLBACK_REASON,
      });
    }
  }
  return out;
}

/** AC1. Whether the picker may submit this selection at all. */
export function selectionIsCoherent(selected: readonly CopyCategory[]): boolean {
  return findUnmetDependencies(selected).length === 0;
}

/**
 * Not an error, and not part of FR26. An account with no pipelines is legal, was
 * asked for, and is close to unusable, so it is worth one sentence before creation
 * rather than a support question afterwards. Suppressed while a hard dependency is
 * unmet, because two messages about the same checkbox is noise.
 */
export function shouldWarnAboutSparseAccount(selected: readonly CopyCategory[]): boolean {
  return (
    selected.length > 0 &&
    !selected.includes('pipelines') &&
    selectionIsCoherent(selected)
  );
}
